import axios from 'axios';

// Ensure API URL always includes /api and handles trailing slashes
const getApiUrl = () => {
  let baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  // Remove trailing slash if present
  baseUrl = baseUrl.replace(/\/$/, '');
  // Add /api if not already present
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
};

const API = getApiUrl();

/**
 * Get authorization header
 */
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Generate a signed URL for viewing/downloading a file from S3
 * @param {string} s3Key - The S3 key/path of the file
 * @returns {Promise<string>} - The signed URL
 */
export const getSignedUrl = async (s3Key) => {
  if (!s3Key) return null;
  
  // If it's already a full URL (mock or old format), return as is
  if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
    return s3Key;
  }
  
  try {
    const response = await axios.get(`${API}/files/signed-url`, {
      params: { s3_key: s3Key },
      headers: getAuthHeader()
    });
    return response.data.url;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return null;
  }
};

/**
 * Open a file in a new tab using signed URL
 * @param {string} s3Key - The S3 key/path of the file
 * @param {string} filename - Optional filename for fallback
 */
export const openFile = async (s3Key, filename = 'document') => {
  const signedUrl = await getSignedUrl(s3Key);
  if (signedUrl) {
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  } else {
    console.error('Failed to generate signed URL for file');
  }
};

/**
 * Extract file extension from S3 key
 * S3 key format: folder/{uuid}_{original_filename}
 */
const getExtensionFromS3Key = (s3Key) => {
  if (!s3Key) return '';
  // Extract filename from S3 key (everything after the last /)
  const filename = s3Key.split('/').pop() || '';
  // Extract extension (everything after the last .)
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return filename.substring(lastDotIndex);
};

/**
 * Get file extension from Content-Type
 */
const getExtensionFromContentType = (contentType) => {
  const typeMap = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'application/pdf': '.pdf',
    'application/octet-stream': ''
  };
  return typeMap[contentType] || '';
};

/**
 * Download a file using signed URL
 * @param {string} s3Key - The S3 key/path of the file
 * @param {string} filename - The base filename for download (without extension)
 */
export const downloadFile = async (s3Key, filename) => {
  const signedUrl = await getSignedUrl(s3Key);
  if (signedUrl) {
    try {
      // Fetch the file as a blob to force download
      const response = await fetch(signedUrl);
      const blob = await response.blob();
      
      // Determine file extension
      let extension = getExtensionFromS3Key(s3Key);
      if (!extension) {
        // Fallback to Content-Type if extension not in S3 key
        extension = getExtensionFromContentType(response.headers.get('content-type') || blob.type);
      }
      
      // Remove existing extension from filename if present, then add correct extension
      let downloadFilename = filename || 'download';
      const lastDotIndex = downloadFilename.lastIndexOf('.');
      if (lastDotIndex !== -1) {
        downloadFilename = downloadFilename.substring(0, lastDotIndex);
      }
      downloadFilename = downloadFilename + extension;
      
      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to direct link
      const link = document.createElement('a');
      link.href = signedUrl;
      // Extract extension from S3 key for fallback
      const extension = getExtensionFromS3Key(s3Key);
      const downloadFilename = (filename || 'download').replace(/\.[^/.]+$/, '') + extension;
      link.download = downloadFilename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } else {
    console.error('Failed to generate signed URL for download');
  }
};

/**
 * Component for rendering a file link with signed URL
 * Usage: <FileLink s3Key={fileKey} label="View Document" />
 */
export const FileLink = ({ s3Key, label = 'View', className = '' }) => {
  const handleClick = async (e) => {
    e.preventDefault();
    await openFile(s3Key);
  };
  
  return (
    <a
      href="#"
      onClick={handleClick}
      className={className || 'text-blue-600 hover:underline'}
    >
      {label}
    </a>
  );
};
